/**
 * Exception-type fetch for the dynamic exception route - resolves the
 * `exception_types` record (header copy, breadcrumb, data_domain for the
 * drawer tuple and the domain fetcher dispatch). Skipped while a legacy
 * code redirect is in flight so the deprecated code never triggers a
 * needless backend call.
 *
 * Extracted from `DynamicExceptionRoute.tsx` as a pure structural move -
 * state and effect are byte-identical to the inline originals.
 */
import { useEffect, useState } from 'react'
import inspectorApi from '../services/inspector/api'

export interface ExceptionType {
  code: string
  name: string
  name_short: string
  /** One-line overview - the exception page subtitle. */
  overview?: string
  /** Drawer "Why this was flagged" closing copy (not rendered here). */
  why_flagged?: string
  data_domain: string
  exception_type?: string
  business_area?: string
  display_columns?: string[]
  function_name?: string
  visible_to_roles?: string[] | null
  action_policy?: { allow_ignore?: boolean } | null
}

export function useExceptionTypeFetch({
  fpCode,
  exceptionCode,
  pendingRedirect,
}: {
  fpCode?: string
  exceptionCode?: string
  /** Truthy while a legacy-code redirect is in flight - skips the fetch. */
  pendingRedirect: object | null
}) {
  const [exceptionType, setExceptionType] = useState<ExceptionType | null>(null)
  const [typeLoading, setTypeLoading] = useState(true)
  const [typeError, setTypeError] = useState<string | null>(null)

  useEffect(() => {
    if (!fpCode || !exceptionCode) {
      setTypeError('Invalid route parameters')
      setTypeLoading(false)
      return
    }
    if (pendingRedirect) {
      // Skip the fetch; navigation is in flight.
      return
    }
    let cancelled = false
    setTypeLoading(true)
    setTypeError(null)
    inspectorApi
      .getExceptionType(exceptionCode)
      .then((response) => {
        if (cancelled) return
        if (!response) throw new Error('Exception type not found')
        setExceptionType(response)
      })
      .catch((err: any) => {
        if (cancelled) return
        console.error('Failed to fetch exception type:', err)
        setTypeError(err.message || 'Failed to load exception type')
      })
      .finally(() => {
        if (!cancelled) setTypeLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fpCode, exceptionCode, pendingRedirect])

  return { exceptionType, typeLoading, typeError }
}
